import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "./ui/card";
import {
  Users,
  CreditCard,
  Package,
  Target,
  Bot,
  GraduationCap,
  Trophy,
  Settings,
  Zap,
  FileText,
  Footprints,
  Compass,
} from "lucide-react";

type TableData = { count?: number };
type TablesMap = Record<string, TableData | undefined>;

type SectionEntry = {
  key: string;
  /** Clé i18n du libellé (admin.*) — prioritaire sur `label`. */
  labelKey?: string;
  /** Libellé brut (entrées historiques non i18n). */
  label?: string;
  /** Réservé aux ADM (super admin Kestio). */
  admOnly?: boolean;
};

type Section = {
  id: string;
  title: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  iconBg: string;
  iconColor: string;
  tables: Array<SectionEntry>;
};

const SECTIONS: Section[] = [
  {
    id: "accounts",
    title: "Comptes & Utilisateurs",
    Icon: Users,
    iconBg: "#A6D3F9",
    iconColor: "#384872",
    tables: [
      { key: "users", label: "Utilisateurs" },
      { key: "client_accounts", label: "Comptes clients" },
      { key: "__portfolios__", label: "Portefeuilles consultants", admOnly: true },
      { key: "user_entity_access", label: "Portefeuilles (table)" },
      { key: "user_memories", label: "Mémoires utilisateur" },
      { key: "__usage_analytics__", label: "Suivi des utilisations" },
    ],
  },
  {
    id: "licences",
    title: "Licences & Abonnements",
    Icon: CreditCard,
    iconBg: "#F49390",
    iconColor: "#C0423F",
    tables: [
      { key: "licences", label: "Produits (abos & packs)" },
      { key: "user_licences", label: "Licences utilisateurs" },
      { key: "simulator_licences", label: "Licences par simulateur" },
      { key: "subscriptions", label: "Abonnements Zoho" },
      { key: "trial_invitations", label: "Offres d'essai" },
      { key: "trial_coupons", label: "Coupons" },
    ],
  },
  {
    id: "content",
    title: "Contenu client",
    Icon: Package,
    iconBg: "#7CBF94",
    iconColor: "#1E7A3C",
    tables: [
      { key: "offers", label: "Offres" },
      { key: "personae", label: "Personae" },
      { key: "__personality_styles__", label: "Styles de personnalité", admOnly: true },
      { key: "targets", label: "Cibles / Cas de simulation" },
      { key: "target_simulator_fit", label: "Compatibilité cas ↔ simulateur", admOnly: true },
      { key: "case_simulator_brief", label: "Briefs cas ↔ simulateur", admOnly: true },
      { key: "case_scenario_translations", label: "Cas génériques traduits", admOnly: true },
      { key: "__banque_de_cas__", label: "Banque de cas génériques" },
      { key: "categories", label: "Catégories" },
    ],
  },
  {
    id: "criteria",
    title: "Contextes et Évaluations",
    Icon: Target,
    iconBg: "#988EF2",
    iconColor: "#5A4BBE",
    tables: [
      { key: "evaluation_criteria", label: "Critères d'évaluation" },
      { key: "__catalog_contexts__", label: "Catalogue contextes" },
      { key: "__eval_criteria_matrix__", labelKey: "admin.criteriaMatrix", admOnly: true },
      { key: "__kestio_knowledge__", labelKey: "admin.kestioCorpus", admOnly: true },
      { key: "__path_templates__", label: "Modèles de parcours", admOnly: true },
      { key: "__drill__", label: "S'exercer — fiches & capsules", admOnly: true },
    ],
  },
  {
    id: "drill",
    title: "S'exercer (exercices)",
    Icon: Footprints,
    iconBg: "#7CBF94",
    iconColor: "#1E7A3C",
    tables: [
      { key: "drill_sessions", label: "Sessions d'exercices", admOnly: true },
      { key: "drill_messages", label: "Transcripts d'exercices", admOnly: true },
      { key: "user_capsule_progress", label: "Complétion capsules", admOnly: true },
      { key: "exercise_sheets", label: "Fiches d'exercice", admOnly: true },
      { key: "learning_capsules", label: "Capsules learning", admOnly: true },
      { key: "capsule_links", label: "Liaisons capsules", admOnly: true },
      { key: "exercise_sheet_methodo_sheets", label: "Liaisons méthodo", admOnly: true },
      { key: "activity_sectors", label: "Secteurs d'activité", admOnly: true },
      { key: "drill_sector_rotation", label: "Rotation secteurs", admOnly: true },
    ],
  },
  {
    id: "ai",
    title: "Simulateurs & IA",
    Icon: Bot,
    iconBg: "#F6C847",
    iconColor: "#926B00",
    tables: [
      { key: "simulators", label: "Simulateurs" },
      { key: "scenarios", label: "Scénarios" },
      { key: "scenario_requests", label: "Demandes de scénarios" },
      { key: "scenario_chat_messages", label: "Messages chat scénario" },
      { key: "ai_prompts", label: "Prompts IA" },
      { key: "n8n_workflows", label: "Workflows N8N" },
      { key: "__llm_routes__", labelKey: "admin.llmRoutes", admOnly: true },
      { key: "__intent_contexts__", label: "Situations manager", admOnly: true },
      { key: "__relance_rules__", labelKey: "admin.relanceRules", admOnly: true },
      { key: "__program_rules__", label: "Routeur — priorité de la semaine", admOnly: true },
      { key: "__reusability_kpis__", label: "KPIs réutilisabilité" },
    ],
  },
  {
    id: "sessions",
    title: "Sessions & Feedback",
    Icon: GraduationCap,
    iconBg: "#A6D3F9",
    iconColor: "#384872",
    tables: [
      { key: "sessions", label: "Sessions" },
      { key: "messages", label: "Messages (call + coaching)" },
      { key: "audio_sessions", label: "Sessions audio" },
      { key: "feedbacks", label: "Feedbacks" },
      { key: "feedback_scores", label: "Scores de feedback" },
      { key: "evaluation_criteria_translations", label: "Critères traduits" },
      { key: "__coaching_briefs__", labelKey: "admin.coachingBriefs" },
    ],
  },
  {
    id: "challenges",
    title: "Challenges",
    Icon: Trophy,
    iconBg: "#7CBF94",
    iconColor: "#1E7A3C",
    tables: [
      { key: "challenges", label: "Challenges" },
      { key: "challenge_teams", label: "Équipes" },
      { key: "challenge_participants", label: "Participants" },
      { key: "challenge_notifications", label: "Notifications" },
      { key: "__badges__", label: "Banque de badges" },
      { key: "__inter_challenges__", label: "Challenges inter" },
    ],
  },
  {
    // Le cockpit manager (cf. spec/cockpit-manager.md) n'avait aucune tuile ici :
    // ses tables n'étaient atteignables que par la liste latérale, et trois
    // d'entre elles n'étaient enregistrées nulle part.
    id: "cockpit",
    title: "Cockpit & campagnes",
    Icon: Compass,
    iconBg: "#A6D3F9",
    iconColor: "#384872",
    tables: [
      { key: "cockpit_signals", label: "Alertes cockpit" },
      { key: "campaigns", label: "Campagnes" },
      { key: "campaign_items", label: "Briques de campagne" },
      { key: "manager_nudges", label: "Relances manager" },
      { key: "planned_sessions", label: "Sessions planifiées" },
      { key: "campaign_member_rhythm", label: "Rythmes de campagne" },
      { key: "user_session_streak", label: "Séries de sessions" },
      { key: "competency_snapshots", label: "Photos de niveau" },
      { key: "manager_reviews", label: "Revues du lundi" },
    ],
  },
  {
    id: "system",
    title: "Système & Audit",
    Icon: Settings,
    iconBg: "#e5e7eb",
    iconColor: "#475569",
    tables: [
      { key: "__trash__", label: "Corbeille" },
      { key: "__audit__", label: "Audit log" },
      { key: "__request_log__", label: "Journal des requêtes" },
      { key: "__cost_dashboard__", labelKey: "admin.costDashboard" },
    ],
  },
  {
    id: "documentation",
    title: "Documentation",
    Icon: FileText,
    iconBg: "#eef1fa",
    iconColor: "#5A4BBE",
    tables: [
      { key: "__tech_doc__", label: "Documentation technique", admOnly: true },
      { key: "__legal_documents__", labelKey: "admin.legalDocuments.tile", admOnly: true },
    ],
  },
];

type ActionItem = {
  key: string;
  /** Clé i18n du libellé. */
  labelKey: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  onClick: () => void;
  disabled?: boolean;
};

export function AdminHomeGrid({
  tables,
  onSelectTable,
  userProfile,
  actions,
}: {
  tables: TablesMap;
  onSelectTable: (key: string) => void;
  userProfile?: string;
  actions?: ActionItem[];
}) {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold" style={{ color: "#384872" }}>
          Administration BizCoach
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Accédez aux tables de la base de données, regroupées par domaine fonctionnel.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {actions && actions.length > 0 && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2.5">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0"
                  style={{ backgroundColor: "#F6C847" }}
                >
                  <Zap className="h-4 w-4" style={{ color: "#926B00" }} />
                </div>
                <span className="font-semibold text-sm" style={{ color: "#384872" }}>
                  {t("admin.actionsSection")}
                </span>
              </div>
            </CardHeader>
            <CardContent className="!pt-0 !pb-3">
              <div className="flex flex-col -mx-2">
                {actions.map((a) => {
                  const Icon = a.Icon;
                  return (
                    <button
                      key={a.key}
                      onClick={a.onClick}
                      disabled={a.disabled}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-left text-sm text-slate-700 group transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-slate-400 group-hover:text-[#384872]" />
                      <span className="group-hover:text-[#384872] truncate">
                        {t(a.labelKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {SECTIONS.map((section) => {
          const Icon = section.Icon;
          const entries = section.tables.filter(
            (e) => !e.admOnly || userProfile === "ADM"
          );
          if (entries.length === 0) return null;
          return (
            <Card key={section.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0"
                    style={{ backgroundColor: section.iconBg }}
                  >
                    <Icon className="h-4 w-4" style={{ color: section.iconColor }} />
                  </div>
                  <span className="font-semibold text-sm" style={{ color: "#384872" }}>
                    {section.title}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="!pt-0 !pb-3">
                <div className="flex flex-col -mx-2">
                  {entries.map((entry) => {
                    const count = tables[entry.key]?.count;
                    const label = entry.labelKey ? t(entry.labelKey) : entry.label;
                    return (
                      <button
                        key={entry.key}
                        onClick={() => onSelectTable(entry.key)}
                        className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-50 text-left text-sm text-slate-700 group transition-colors"
                      >
                        <span className="group-hover:text-[#384872] truncate">{label}</span>
                        {count !== undefined && (
                          <span className="text-xs text-slate-400 group-hover:text-slate-600 ml-2 flex-shrink-0">
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
